"use client";

import { useRef } from "react";
import { ChevronLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import { RESUME_UPLOAD_ACCEPT } from "@artha/api-contracts";
import { BottomSheet } from "@artha/ui";
import { CountryRestrictionModal } from "@/modules/geolocation";
import type { ResumePersonalizationSheetProps } from "../personalization.types";
import { useResumePersonalizationSheet } from "../hooks/resume/useResumePersonalizationSheet";
import { ManageStep } from "./sheet-steps/ManageStep";
import { EmailLinkSentStep } from "./sheet-steps/EmailLinkSentStep";
import { OtpStep } from "./sheet-steps/OtpStep";
import { ParsingStep } from "./sheet-steps/ParsingStep";
import { ReviewStep } from "./sheet-steps/ReviewStep";
import { SignInStep } from "./sheet-steps/SignInStep";
import { PhoneSignInStep } from "./sheet-steps/PhoneSignInStep";
import { UploadStep } from "./sheet-steps/UploadStep";
import { FitResultStep } from "./sheet-steps/FitResultStep";
import {
  PERSONALIZATION_SHEET_CLASS_NAME,
  PERSONALIZATION_SHEET_CONTENT_CLASS_NAME,
  PERSONALIZATION_SHEET_OVERLAY_CLASS_NAME,
  TALENT_NETWORK_UPLOAD_COPY,
} from "../personalization.constants";

export function ResumePersonalizationSheet({
  isOpen,
  onClose,
  onComplete,
  onParsed,
  closeOnComplete,
  continuation,
  initialStep,
  signInMessage,
  controller,
  mode = "personalization",
  creatorName,
  creatorUsername,
  job,
  skipFitCheck = false,
  onViewJobs,
}: ResumePersonalizationSheetProps) {
  const t = useTranslations("profile.resumePersonalization");

  if (!isOpen) {
    return (
      <BottomSheet isOpen={false} onClose={onClose} title={t("title")}>
        {null}
      </BottomSheet>
    );
  }

  return (
    <OpenResumePersonalizationSheet
      key={controller.isSessionLoading ? "loading" : "ready"}
      onClose={onClose}
      onComplete={onComplete}
      onParsed={onParsed}
      closeOnComplete={closeOnComplete}
      continuation={continuation}
      initialStep={initialStep}
      signInMessage={signInMessage}
      controller={controller}
      mode={mode}
      creatorName={creatorName}
      creatorUsername={creatorUsername}
      job={job}
      skipFitCheck={skipFitCheck}
      onViewJobs={onViewJobs}
    />
  );
}
function OpenResumePersonalizationSheet({
  onClose,
  onComplete,
  onParsed,
  closeOnComplete,
  continuation,
  initialStep,
  signInMessage,
  controller,
  mode = "personalization",
  creatorName,
  creatorUsername,
  job,
  skipFitCheck = false,
  onViewJobs,
}: Omit<ResumePersonalizationSheetProps, "isOpen">) {
  const t = useTranslations("profile.resumePersonalization");
  const tApply = useTranslations("jobDetails.resumeEligibility");
  const isApplyEligibility = mode === "apply_eligibility";
  const isTalentNetwork = mode === "talent_network";
  const inputRef = useRef<HTMLInputElement | null>(null);
  const sheet = useResumePersonalizationSheet({
    onClose,
    onComplete,
    onParsed,
    closeOnComplete,
    controller,
    initialStep: isApplyEligibility
      ? controller.isAuthenticated && controller.editableProfile
        ? "parsing"
        : "upload"
      : controller.isSessionLoading
        ? undefined
        : initialStep,
    uploadSource: isApplyEligibility
      ? "apply_gate"
      : isTalentNetwork
        ? "talent_network"
        : "jobs_page",
    job,
    skipFitCheck,
  });

  if (sheet.step === "no_match") {
    return (
      <CountryRestrictionModal
        isOpen
        title={tApply("noMatchTitle")}
        subtitle={tApply("noMatchDescription")}
        actionLabel={tApply("viewJobs")}
        onClose={onClose}
        onShowSimilarJobs={onViewJobs || onClose}
      />
    );
  }

  return (
    <BottomSheet
      isOpen
      onClose={onClose}
      title={
        continuation ? continuation.title : isTalentNetwork && sheet.step === "sign_in" ? "Sign in to join the network" : sheet.step === "manage" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold text-[#344054] leading-snug">
              {t("manage.title")}
            </span>
            <span className="block text-xs font-normal text-[#667085] leading-normal">
              {t("manage.description")}
            </span>
          </div>
        ) : sheet.step === "upload" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold text-[#344054] leading-snug">
              {isTalentNetwork
                ? creatorName ? `Join ${creatorName}'s Talent Network` : "Join the Talent Network"
                : isApplyEligibility ? tApply("title") : t("title")}
            </span>
            <span className="block text-xs font-normal text-[#667085] leading-normal">
              {isTalentNetwork
                ? TALENT_NETWORK_UPLOAD_COPY.subtitle
                : isApplyEligibility ? tApply("subtitle") : t("subtitle")}
            </span>
          </div>
        ) : sheet.step === "review" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold text-[#344054] leading-snug">
              {isApplyEligibility ? tApply("reviewTitle") : t("review.title")}
            </span>
            <span className="block text-xs font-normal text-[#667085] leading-normal">
              {isApplyEligibility
                ? tApply("reviewDescription")
                : t("review.description")}
            </span>
          </div>
        ) : sheet.step === "parsing" && isTalentNetwork ? (
          TALENT_NETWORK_UPLOAD_COPY.parsingTitle(creatorName || "the creator")
        ) : sheet.step === "otp" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold text-[#344054] leading-snug">
              {isTalentNetwork
                ? "Verify contact details"
                : isApplyEligibility ? tApply("otpTitle") : t("otp.title")}
            </span>
            <span className="block text-xs font-normal text-[#667085] leading-normal">
              {!isApplyEligibility && controller.verificationMode === "login"
                ? "Open the link in your email to sign in and continue."
                : isApplyEligibility ? tApply("otpSubtitle") : t("otp.subtitle")}
            </span>
          </div>
        ) : sheet.step === "unavailable" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold leading-snug text-[#344054]">
              {tApply("unavailableTitle")}
            </span>
          </div>
        ) : isApplyEligibility ? (
          tApply("title")
        ) : (
          t("title")
        )
      }
      headerAction={
        !continuation && sheet.showBack ? (
          <button
            type="button"
            aria-label={t("actions.goBack")}
            disabled={controller.isRequestingCode}
            className="rounded-full border border-black bg-white p-1.5 disabled:cursor-wait disabled:opacity-50"
            onClick={sheet.goBack}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        ) : undefined
      }
      desktopPlacement="bottom"
      className={PERSONALIZATION_SHEET_CLASS_NAME}
      contentClassName={PERSONALIZATION_SHEET_CONTENT_CLASS_NAME}
      overlayClassName={PERSONALIZATION_SHEET_OVERLAY_CLASS_NAME}
    >
      {continuation ? continuation.content : <div className="space-y-5">
        {sheet.error || (sheet.step === "sign_in" && signInMessage) ? (
          <p
            role="alert"
            className="rounded-[10px] border border-[#9D1C1C] bg-[#FFF0F0] px-3 py-2 text-sm font-semibold text-[#9D1C1C]"
          >
            {sheet.step === "sign_in" && signInMessage ? signInMessage : sheet.error}
          </p>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept={RESUME_UPLOAD_ACCEPT}
          className="sr-only"
          aria-label={t("upload.fileInputLabel")}
          onChange={(event) => {
            sheet.chooseFile(event.target.files?.[0] || null);
            event.target.value = "";
          }}
        />

        {sheet.step === "upload" ? (
          <UploadStep
            selectedFile={sheet.selectedFile}
            isUploading={controller.isUploading}
            hasUploadConsent={sheet.hasUploadConsent}
            onUploadConsentChange={sheet.setHasUploadConsent}
            onChooseFile={() => inputRef.current?.click()}
            onNext={() => void sheet.upload()}
            nextLabel={
              isTalentNetwork
                ? TALENT_NETWORK_UPLOAD_COPY.cta
                : onParsed ? "Review my profile"
                : isApplyEligibility ? tApply("checkMyMatch") : undefined
            }
            showSignIn={!controller.isAuthenticated}
            isTalentNetwork={isTalentNetwork}
            onSignIn={() => {
              sheet.setStep("sign_in");
            }}
          />
        ) : null}
        {sheet.step === "parsing" ? (
          <ParsingStep
            restoring={controller.isSessionLoading}
            title={isTalentNetwork ? TALENT_NETWORK_UPLOAD_COPY.parsingMessage : undefined}
            description={isTalentNetwork ? TALENT_NETWORK_UPLOAD_COPY.parsingDescription : undefined}
          />
        ) : null}
        {sheet.step === "review" ? (
          <ReviewStep
            form={sheet.reviewForm}
            isRequestingCode={controller.isRequestingCode}
            onChange={sheet.setReviewForm}
            onSubmit={() => void sheet.review()}
            submitLabel={
              isTalentNetwork
                ? "Continue to verification"
                : isApplyEligibility ? tApply("checkMyMatch") : undefined
            }
            requestingLabel={
              isApplyEligibility ? tApply("checkingFit") : undefined
            }
          />
        ) : null}
        {sheet.step === "sign_in" ? (
          isTalentNetwork ? <PhoneSignInStep
            username={creatorUsername || ""}
            creatorId={controller.creatorId}
            creatorName={creatorName}
            onUploadResume={sheet.showUpload}
            onComplete={async () => {
              await controller.refreshSession().catch(() => undefined);
              await onComplete?.();
              if (closeOnComplete !== false) onClose();
            }}
          /> : <SignInStep
            verificationMethod={isApplyEligibility ? "code" : "link"}
            email={sheet.signInEmail}
            isRequestingCode={controller.isRequestingCode}
            onEmailChange={sheet.setSignInEmail}
            onSubmit={() => void sheet.signIn()}
          />
        ) : null}
        {sheet.step === "otp" && !isApplyEligibility && controller.verificationMode === "login" ? (
          <EmailLinkSentStep email={controller.maskedEmail || sheet.verificationEmail}
            isRequestingCode={controller.isRequestingCode} onResend={() => void sheet.resend()} />
        ) : sheet.step === "otp" ? (
          <OtpStep
            submitLabel={isTalentNetwork ? "Verify email and continue" : undefined}
            code={sheet.code}
            email={controller.maskedEmail || sheet.verificationEmail}
            isRequestingCode={controller.isRequestingCode}
            isVerifying={controller.isVerifying}
            onCodeChange={sheet.setCode}
            onSubmit={() => void sheet.verify()}
            onResend={() => void sheet.resend()}
          />
        ) : null}
        {sheet.step === "manage" ? (
          <ManageStep
            profile={controller.editableProfile}
            isLoggingOut={controller.isLoggingOut}
            onUpdate={sheet.showUpload}
            onLogout={() => void sheet.logout()}
          />
        ) : null}
        {sheet.step === "unavailable" ? (
          <FitResultStep
            onRetry={sheet.retryFit}
          />
        ) : null}
      </div>}
    </BottomSheet>
  );
}
